// src/features/campaigns/components/CampaignForm.tsx
// ============================================================================
// FORMULARIO DE CAMPAÑA
// ============================================================================
//
// v2.0 - Imagen adjunta opcional (ImageAttachment)
// v3.0 - Campaña personalizada (type: 'custom')
// v3.1 - Filtros inteligentes (activityMonths, minPoints)
// v3.2 - Filtro por etiquetas (tags)
// v3.2.1 - Sección de etiquetas siempre visible (con estado vacío guía)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Save, Users, Calendar, UserMinus, MessageSquare, Check, Crown, Mail,
  UserPlus, Search, X, Download, Loader2, Filter, Tag, AlertTriangle, Pin, RefreshCw, Send, UserCheck
} from 'lucide-react';
import { CampaignFormData, CampaignType, CampaignFilter, CampaignMode, CampaignDispatch, CampaignChannel, EmailDesign } from '../types/campaign';
import { useError } from '../../../context/ErrorContext';
import { useAuth } from '../../../context/AuthContext';
import { isPro } from '../../../utils/planPermissions';
// Re-cableado a lealify-base (Inc10): audiencia/etiquetas/import desde base, fuera Firestore/esapi.
import { baseApi } from '../../../api/baseClient';
import { clientsApi } from '../../../api/clientsApi';
import { campaignsApi } from '../../../api/campaignsApi';

// ── v2.0: Imagen adjunta ──
import ImageAttachment from '../../../components/shared/ImageAttachment';

// ── Diseño del correo: galería genérica (17-jul). Cada negocio elige entre 3 diseños
// y la MUESTRA se renderiza en el backend con su marca real (logo/colores) vía
// POST /campaigns/email-preview → iframe srcDoc. Sin caja de cupón si no hay texto.
const EMAIL_DESIGN_OPTIONS: Array<{ value: EmailDesign; emoji: string; label: string; hint: string }> = [
  { value: 'simple', emoji: '✉️', label: 'Sencillo', hint: 'Limpio y directo' },
  { value: 'festive', emoji: '🎉', label: 'Festivo', hint: 'Banda de celebración' },
  { value: 'coupon', emoji: '🎟️', label: 'Cupón', hint: 'Oferta destacada' },
];

const EmailDesignPicker: React.FC<{
  design: EmailDesign; highlight: string; subject: string; body: string;
  onDesign: (d: EmailDesign) => void; onHighlight: (h: string) => void;
}> = ({ design, highlight, subject, body, onDesign, onHighlight }) => {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Muestra en vivo: carga al expandir y se refresca (debounce 500ms) al cambiar
  // diseño/asunto/mensaje/cupón — así ven exactamente lo que va a salir.
  useEffect(() => {
    if (!open) return;
    let dead = false;
    setLoading(true);
    const t = setTimeout(() => {
      campaignsApi.previewEmail({ design, subject, body, highlight })
        .then((r) => { if (!dead) setHtml(r.html); })
        .catch(() => { if (!dead) setHtml(null); })
        .finally(() => { if (!dead) setLoading(false); });
    }, 500);
    return () => { dead = true; clearTimeout(t); };
  }, [open, design, highlight, subject, body]);

  return (
    <div>
      <label className="block text-sm font-medium text-content-2">Diseño del correo</label>
      <div className="mt-2 grid grid-cols-3 gap-2">
        {EMAIL_DESIGN_OPTIONS.map((o) => (
          <button key={o.value} type="button" onClick={() => onDesign(o.value)} aria-pressed={design === o.value}
            className={`rounded-lg border p-3 text-center transition ${design === o.value ? 'border-accent ring-2 ring-accent/30 bg-accent/5' : 'border-line hover:border-content-3'}`}>
            <div className="text-xl">{o.emoji}</div>
            <div className="mt-1 text-xs font-semibold text-content-1">{o.label}</div>
            <div className="text-[10px] text-content-3">{o.hint}</div>
          </button>
        ))}
      </div>
      {design === 'coupon' && (
        <input type="text" id="campaign-email-highlight" value={highlight} maxLength={160}
          onChange={(e) => onHighlight(e.target.value)}
          className="mt-2 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          placeholder={'Texto del cupón, ej: "2×1 en tu próxima visita"'} />
      )}
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-accent hover:underline">
        <Mail size={14} /> {open ? 'Ocultar muestra' : 'Ver muestra con mi marca'}
      </button>
      {open && (
        <div className="mt-2 overflow-hidden rounded-lg border border-line bg-white">
          {loading && <div className="flex items-center gap-2 p-3 text-sm text-content-3"><Loader2 size={14} className="animate-spin" /> Generando muestra…</div>}
          {html
            ? <iframe srcDoc={html} title="Muestra del correo" className="w-full" style={{ height: 460, border: 0 }} sandbox="" />
            : (!loading && <div className="p-3 text-sm text-content-3">No se pudo generar la muestra.</div>)}
        </div>
      )}
    </div>
  );
};

// ── v3.0: Buscador de clientes ──
import SearchInput, { SearchResultItem } from '../../../components/common/SearchInput';

// ============================================================================
// INTERFACES
// ============================================================================

interface SelectedClient {
  id: string;
  name: string | null;
  phone: string;
}

interface ImportableCampaign {
  id: string;
  name: string;
  totalMessagesSent: number;
}

interface AvailableTag {
  name: string;
  count: number;
}

interface CampaignFormProps {
  initialData?: CampaignFormData;
  onSubmit: (data: CampaignFormData) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  isPremiumWaha?: boolean;
}

// ============================================================================
// COMPONENTE
// ============================================================================

const CampaignForm: React.FC<CampaignFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
  isPremiumWaha = false,
}) => {
  // `validationErrors` no existe en el contexto: el bloque inline que lo pintaba nunca podía
  // renderizar. Los errores de validación se ven en el ErrorDisplay global (addValidationError).
  const { addValidationError, clearErrors } = useError();
  const { currentUser } = useAuth();
  // El canal EMAIL es función PRO (el backend también lo exige al programar).
  const canEmail = isPro(currentUser?.subscription);

  const defaultFormData: CampaignFormData = {
    name: '',
    description: '',
    type: 'all',
    channel: 'whatsapp',
    emailSubject: '',
    emailDesign: 'simple',
    emailHighlight: '',
    message: 'Hola @nombre, queremos informarte sobre nuestra nueva promoción en @marca. ¡Te esperamos pronto!',
    filters: [],
    image: null,
    selectedClientIds: [],
  };

  const [formData, setFormData] = useState<CampaignFormData>(initialData || defaultFormData);
  // Canal actual (whatsapp por defecto) + setter.
  const channel: CampaignChannel = formData.channel === 'email' ? 'email' : 'whatsapp';
  const setChannel = (ch: CampaignChannel) => setFormData(prev => ({ ...prev, channel: ch }));
  const [birthdayRangeValue, setBirthdayRangeValue] = useState<string>("7");
  // Anticipación mínima: no avisar si ya faltan menos de N días (pre-cumpleaños prudente).
  const [birthdayMinDaysValue, setBirthdayMinDaysValue] = useState<string>("7");
  // Modo fija/dinámica (motor de base). Solo audiencias por REGLA pueden ser dinámicas.
  const [modeValue, setModeValue] = useState<CampaignMode>(initialData?.mode === 'dynamic' ? 'dynamic' : 'fixed');
  // Quién manda: el motor solo o una persona. Hasta el 24-jul-2026 el manager no conocía este
  // campo, así que sus 36 campañas nacieron automáticas — incluidas las de The Cakery, el
  // número que ya perdieron dos veces. Sin esto no se puede estrenar un WhatsApp con calma.
  const [dispatchValue, setDispatchValue] = useState<CampaignDispatch>(initialData?.dispatch === 'assisted' ? 'assisted' : 'auto');

  const [selectedClients, setSelectedClients] = useState<SelectedClient[]>([]);
  const [importableCampaigns, setImportableCampaigns] = useState<ImportableCampaign[]>([]);
  const [selectedImportCampaignId, setSelectedImportCampaignId] = useState<string>('');
  const [importLoading, setImportLoading] = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const [activityMonths, setActivityMonths] = useState<string>('');
  const [minPoints, setMinPoints] = useState<string>('');
  // v9.4: recencia por rango (no vienen hace ENTRE min y max días)
  const [recencyMin, setRecencyMin] = useState<string>('');
  const [recencyMax, setRecencyMax] = useState<string>('');
  // v9.4: cumpleañeros por mes calendario (0-11, '' = sin filtro)
  const [birthdayMonth, setBirthdayMonth] = useState<string>('');

  const [availableTags, setAvailableTags] = useState<AvailableTag[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Aviso de solape con cumpleaños-automático: la config vive en el módulo Birthdays
  // (diferido). Hasta re-cablearlo, no mostramos el aviso. [[project_lealify_plataforma_multiproducto]]
  const birthdayAutoEnabled = false;

  // ============================================================================
  // SINCRONIZAR CON initialData
  // ============================================================================

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setModeValue(initialData.mode === 'dynamic' ? 'dynamic' : 'fixed');
      setDispatchValue(initialData.dispatch === 'assisted' ? 'assisted' : 'auto');

      if (initialData.type === 'birthday') {
        const birthdayRangeFilter = initialData.filters.find(f => f.type === 'birthdayRange');
        const brVal = birthdayRangeFilter ? (String(birthdayRangeFilter.value) === '1' ? '0' : String(birthdayRangeFilter.value)) : null; // legacy "1"≈solo hoy
        const bMinFilter = initialData.filters.find(f => f.type === 'birthdayMinDays');
        setBirthdayMinDaysValue(bMinFilter != null && ['0', '3', '7'].includes(String(bMinFilter.value)) ? String(bMinFilter.value) : '0');
        if (brVal && ["0", "7", "30"].includes(brVal)) {
          setBirthdayRangeValue(brVal);
        } else {
          setBirthdayRangeValue("7");
        }
      } else {
        setBirthdayRangeValue("7");
      }

      if (initialData.type === 'custom' && initialData.selectedClientIds?.length) {
        const restoredClients = initialData.selectedClientIds.map(id => ({
          id,
          name: null,
          phone: '',
        }));
        setSelectedClients(restoredClients);
      } else {
        setSelectedClients([]);
      }

      if (initialData.type === 'custom') {
        const actFilter = initialData.filters.find(f => f.type === 'activityMonths');
        setActivityMonths(actFilter ? String(actFilter.value) : '');
        const ptsFilter = initialData.filters.find(f => f.type === 'minPoints');
        setMinPoints(ptsFilter ? String(ptsFilter.value) : '');
        const tagsFilter = initialData.filters.find(f => f.type === 'tags');
        setSelectedTags(tagsFilter && Array.isArray(tagsFilter.value) ? tagsFilter.value : []);
        const recFilter = initialData.filters.find(f => f.type === 'recencyRange');
        setRecencyMin(recFilter?.value?.min != null ? String(recFilter.value.min) : '');
        setRecencyMax(recFilter?.value?.max != null ? String(recFilter.value.max) : '');
        const bMonthFilter = initialData.filters.find(f => f.type === 'birthdayMonth');
        setBirthdayMonth(bMonthFilter?.value != null && bMonthFilter.value !== '' ? String(bMonthFilter.value) : '');
      } else {
        setActivityMonths('');
        setMinPoints('');
        setSelectedTags([]);
        setRecencyMin('');
        setRecencyMax('');
        setBirthdayMonth('');
      }
    } else {
      setFormData(defaultFormData);
      setBirthdayRangeValue("7");
      setSelectedClients([]);
      setActivityMonths('');
      setMinPoints('');
      setSelectedTags([]);
      setRecencyMin('');
      setRecencyMax('');
      setBirthdayMonth('');
    }
  }, [initialData]);

  // ============================================================================
  // Hidratar nombres de clientes al editar campaña custom
  // ============================================================================

  useEffect(() => {
    if (formData.type !== 'custom' || selectedClients.length === 0) return;

    const idsWithoutName = selectedClients.filter(c => !c.name).map(c => c.id);
    if (idsWithoutName.length === 0) return;

    let cancelled = false;

    const hydrateClientNames = async () => {
      try {
        // Hidrata por id contra base (clientsApi). Pocos clientes seleccionados → N peticiones OK.
        const results = await Promise.all(
          idsWithoutName.map(async (id) => {
            try { return await clientsApi.getClient(id); } catch { return null; }
          })
        );
        if (cancelled) return;
        const clientMap = new Map(results.filter(Boolean).map((c: any) => [String(c.id), c]));
        setSelectedClients(prev =>
          prev.map(c => {
            const details: any = clientMap.get(String(c.id));
            if (details && !c.name) {
              return { ...c, name: details.visitorName || null, phone: details.phoneNumber || '' };
            }
            return c;
          })
        );
      } catch (error) {
        console.error('Error hidratando nombres de clientes:', error);
      }
    };

    hydrateClientNames();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.type, selectedClients.length]);

  // ============================================================================
  // Cargar campañas disponibles para importar
  // ============================================================================

  useEffect(() => {
    if (formData.type !== 'custom' || !currentUser?.businessId) {
      setImportableCampaigns([]);
      return;
    }

    const fetchCampaigns = async () => {
      setCampaignsLoading(true);
      try {
        const list = await campaignsApi.list();
        // Solo campañas que ya enviaron algo (tienen participantes para importar).
        const importable: ImportableCampaign[] = list
          .filter(c => (c.totalMessagesSent || 0) > 0)
          .map(c => ({ id: c.id, name: c.name || 'Sin nombre', totalMessagesSent: c.totalMessagesSent || 0 }));
        setImportableCampaigns(importable);
      } catch (error) {
        console.error('Error cargando campañas para importar:', error);
      } finally {
        setCampaignsLoading(false);
      }
    };

    fetchCampaigns();
  }, [formData.type, currentUser?.businessId]);

  // ============================================================================
  // Cargar etiquetas disponibles del negocio desde ES
  // ============================================================================

  useEffect(() => {
    if (formData.type !== 'custom' || !currentUser?.businessId) {
      setAvailableTags([]);
      return;
    }

    let cancelled = false;

    const fetchTags = async () => {
      setTagsLoading(true);
      try {
        // base /contacts/tags → { tags: [{tag, n}] }
        const data = await baseApi.get<{ tags: Array<{ tag: string; n: number }> }>('/contacts/tags');
        if (cancelled) return;
        setAvailableTags((data.tags || []).map(t => ({ name: t.tag, count: Number(t.n) || 0 })));
      } catch (error) {
        console.error('Error cargando etiquetas:', error);
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    };

    fetchTags();

    return () => { cancelled = true; };
  }, [formData.type, currentUser?.businessId]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ¿La audiencia actual es una REGLA (elegible para modo dinámico)?
  // birthday/inactive siempre; custom solo por filtros (una lista manual de clientes no
  // es regla). all/bestClients nunca (anuncio puntual / foto de ranking).
  const dynamicEligible =
    formData.type === 'birthday' || formData.type === 'inactive' ||
    (formData.type === 'custom' && selectedClients.length === 0);

  const handleTypeChange = (type: CampaignType) => {
    setFormData(prev => ({ ...prev, type }));
    // Si el tipo nuevo no admite dinámica, regresa el modo a fija (evita estados imposibles).
    if (!(type === 'birthday' || type === 'inactive' || type === 'custom')) setModeValue('fixed');
    if (type === 'birthday') {
      const currentBirthdayFilterValue = formData.filters.find(f => f.type === 'birthdayRange')?.value;
      if (!["0", "7", "30"].includes(String(currentBirthdayFilterValue))) {
        setBirthdayRangeValue("7");
      }
    }
    if (type !== 'custom') {
      setSelectedClients([]);
      setActivityMonths('');
      setMinPoints('');
      setSelectedTags([]);
    }
  };

  const handleImageChange = useCallback((base64: string | null) => {
    setFormData(prev => ({ ...prev, image: base64 }));
  }, []);

  const handleClientSelect = useCallback((client: SearchResultItem) => {
    setSelectedClients(prev => {
      if (prev.some(c => c.id === client.id)) return prev;
      return [...prev, {
        id: client.id,
        name: client.name,
        phone: client.phone,
      }];
    });
  }, []);

  const handleRemoveClient = useCallback((clientId: string) => {
    setSelectedClients(prev => prev.filter(c => c.id !== clientId));
  }, []);

  const handleClearAllClients = useCallback(() => {
    setSelectedClients([]);
  }, []);

  const handleImportFromCampaign = useCallback(async () => {
    if (!selectedImportCampaignId || !currentUser?.businessId) return;

    setImportLoading(true);
    try {
      // Participantes = filas de la cola ya enviadas (base). Tomamos sus contact_id.
      const rows = await campaignsApi.queue(selectedImportCampaignId, 'sent');
      const importedIds: string[] = rows.map(r => String(r.contact_id));

      if (importedIds.length === 0) {
        return;
      }

      setSelectedClients(prev => {
        const existingIds = new Set(prev.map(c => c.id));
        const newClients = importedIds
          .filter(id => !existingIds.has(id))
          .map(id => ({ id, name: null as string | null, phone: '' }));

        return [...prev, ...newClients];
      });

      setSelectedImportCampaignId('');
    } catch (error) {
      console.error('Error importando participantes:', error);
    } finally {
      setImportLoading(false);
    }
  }, [selectedImportCampaignId, currentUser?.businessId]);

  const handleToggleTag = useCallback((tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }, []);

  // ── Submit ──

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();

    let hasErrors = false;
    if (!formData.name.trim()) {
      addValidationError('El nombre de la campaña es obligatorio.', 'campaign-name');
      hasErrors = true;
    }

    if (!formData.message.trim()) {
      addValidationError('El mensaje es obligatorio.', 'campaign-message');
      hasErrors = true;
    }

    // Canal EMAIL: asunto obligatorio (el backend también lo exige al programar).
    if (channel === 'email' && !(formData.emailSubject || '').trim()) {
      addValidationError('El asunto del correo es obligatorio.', 'campaign-subject');
      hasErrors = true;
    }
    // Diseño Cupón sin texto = caja que no se pinta; mejor exigirlo aquí.
    if (channel === 'email' && formData.emailDesign === 'coupon' && !(formData.emailHighlight || '').trim()) {
      addValidationError('El diseño Cupón necesita el texto de la oferta (ej. "2×1 en tu próxima visita").', 'campaign-email-highlight');
      hasErrors = true;
    }

    if (formData.type === 'custom') {
      const hasClients = selectedClients.length > 0;
      const hasSmartFilters = activityMonths !== '' || minPoints !== '' || selectedTags.length > 0 || recencyMin !== '' || recencyMax !== '' || birthdayMonth !== '';
      if (!hasClients && !hasSmartFilters) {
        addValidationError('Debes seleccionar clientes, etiquetas o configurar al menos un filtro inteligente.', 'campaign-clients');
        hasErrors = true;
      }
    }

    if (hasErrors) return;

    let updatedFilters: CampaignFilter[] = [];

    if (formData.type === 'birthday') {
      // Ventana [min..max]: con "Solo el día" (max 0) la anticipación mínima no aplica.
      updatedFilters = [
        { type: 'birthdayRange', value: birthdayRangeValue },
        { type: 'birthdayMinDays', value: birthdayRangeValue === '0' ? '0' : birthdayMinDaysValue },
      ];
    } else if (formData.type === 'inactive') {
      const currentInactiveDaysValue = formData.filters.find(f => f.type === 'inactiveDays')?.value;
      updatedFilters = [{ type: 'inactiveDays', value: currentInactiveDaysValue || 30 }];
    } else if (formData.type === 'custom') {
      if (activityMonths !== '') {
        updatedFilters.push({ type: 'activityMonths', value: parseInt(activityMonths, 10) });
      }
      if (minPoints !== '') {
        updatedFilters.push({ type: 'minPoints', value: parseFloat(minPoints) });
      }
      if (selectedTags.length > 0) {
        updatedFilters.push({ type: 'tags', value: selectedTags });
      }
      // v9.4: recencia por rango (no vienen hace entre min y max días)
      if (recencyMin !== '' || recencyMax !== '') {
        updatedFilters.push({
          type: 'recencyRange',
          value: {
            min: recencyMin !== '' ? parseInt(recencyMin, 10) : null,
            max: recencyMax !== '' ? parseInt(recencyMax, 10) : null,
          },
        });
      }
      // v9.4: cumpleañeros por mes calendario (0-11)
      if (birthdayMonth !== '') {
        updatedFilters.push({ type: 'birthdayMonth', value: parseInt(birthdayMonth, 10) });
      }
    } else {
      updatedFilters = [];
    }

    await onSubmit({
      ...formData,
      filters: updatedFilters,
      // Dinámica solo si la audiencia final es una regla (lista manual fuerza fija).
      mode: (dynamicEligible && !(formData.type === 'custom' && selectedClients.length > 0)) ? modeValue : 'fixed',
      // El correo siempre lo manda el sistema: no hay número que cuidar.
      dispatch: channel === 'email' ? 'auto' : dispatchValue,
      image: isPremiumWaha ? (formData.image ?? null) : null,
      selectedClientIds: formData.type === 'custom'
        ? selectedClients.map(c => c.id)
        : undefined,
    });
  };

  // ============================================================================
  // HELPERS
  // ============================================================================

  const getCampaignTypeIcon = (type: CampaignType) => {
    switch (type) {
      case 'birthday': return <Calendar className="h-5 w-5 text-pink-500" />;
      case 'inactive': return <UserMinus className="h-5 w-5 text-orange-500" />;
      case 'all':      return <Users className="h-5 w-5 text-blue-500" />;
      case 'custom':   return <UserPlus className="h-5 w-5 text-purple-500" />;
      default:         return <MessageSquare className="h-5 w-5 text-content-3" />;
    }
  };

  const getBirthdayRangeLabel = () => {
    const min = birthdayRangeValue === '0' ? 0 : Number(birthdayMinDaysValue) || 0;
    switch (birthdayRangeValue) {
      case "0":  return "Se contactará a clientes cuyo cumpleaños sea HOY.";
      case "7":  return min > 0
        ? `Se contactará a clientes a los que les falten entre ${min} y 7 días para su cumpleaños.`
        : "Se contactará a clientes cuyo cumpleaños sea hoy o en los próximos 7 días.";
      case "30": return min > 0
        ? `Se contactará a clientes a los que les falten entre ${min} y 30 días para su cumpleaños.`
        : "Se contactará a clientes cuyo cumpleaños sea hoy o en los próximos 30 días.";
      default:   return "";
    }
  };

  const namedCount = selectedClients.filter(c => c.name).length;
  const importedCount = selectedClients.filter(c => !c.name).length;
  const hasActiveSmartFilters = activityMonths !== '' || minPoints !== '' || selectedTags.length > 0;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <form onSubmit={handleSubmit} className="space-y-6">

      {/* ── Nombre ── */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-content-2">
          Nombre de la Campaña <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          placeholder="Ej: Promoción de Verano"
        />
      </div>

      {/* ── Canal de envío (WhatsApp / Email) ── */}
      <div>
        <label className="block text-sm font-medium text-content-2 mb-1">Canal de envío</label>
        <div className="inline-flex rounded-lg border border-line overflow-hidden">
          <button
            type="button"
            onClick={() => setChannel('whatsapp')}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
              channel === 'whatsapp' ? 'bg-accent text-white' : 'bg-surface-2 text-content-2 hover:bg-surface-3'
            }`}
          >
            <MessageSquare className="h-4 w-4" /> WhatsApp
          </button>
          <button
            type="button"
            disabled={!canEmail}
            onClick={() => canEmail && setChannel('email')}
            title={canEmail ? '' : 'Función PRO'}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-l border-line ${
              channel === 'email' ? 'bg-accent text-white' : 'bg-surface-2 text-content-2 hover:bg-surface-3'
            } ${!canEmail ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Mail className="h-4 w-4" /> Correo
            {!canEmail && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">PRO</span>}
          </button>
        </div>
        {!canEmail && (
          <p className="mt-1 text-xs text-content-3">Las campañas por correo son una función PRO.</p>
        )}
        {channel === 'email' && (
          <div className="mt-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 px-3 py-2.5">
            <Mail className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
            <div className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
              <b>Campaña por correo — 1 solicitud al mes.</b> Se envía a tus clientes con correo (no por WhatsApp).
              Al terminar, esto queda como <b>solicitud</b>: la revisamos y te confirmamos antes de enviarla —
              <b> crearla no la envía</b>.
            </div>
          </div>
        )}
      </div>

      {/* ── Asunto (solo canal Correo) ── */}
      {channel === 'email' && (
        <div>
          <label htmlFor="emailSubject" className="block text-sm font-medium text-content-2">
            Asunto del correo <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="emailSubject"
            name="emailSubject"
            value={formData.emailSubject || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, emailSubject: e.target.value }))}
            maxLength={255}
            className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
            placeholder="Ej: Novedades de esta semana 🎉"
          />
        </div>
      )}

      {/* ── Diseño del correo (galería genérica; la muestra usa la marca del negocio) ── */}
      {channel === 'email' && (
        <EmailDesignPicker
          design={formData.emailDesign || 'simple'}
          highlight={formData.emailHighlight || ''}
          subject={formData.emailSubject || ''}
          body={formData.message || ''}
          onDesign={(d) => setFormData(prev => ({ ...prev, emailDesign: d }))}
          onHighlight={(h) => setFormData(prev => ({ ...prev, emailHighlight: h }))}
        />
      )}

      {/* ── Descripción ── */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-content-2">
          Descripción
        </label>
        <textarea
          id="description"
          name="description"
          rows={2}
          value={formData.description}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
          placeholder="Breve descripción de la campaña"
        />
      </div>

      {/* ── Tipo de campaña ── */}
      <div>
        <label htmlFor="type" className="block text-sm font-medium text-content-2">
          Tipo de Campaña <span className="text-red-500">*</span>
        </label>
        <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {(['all', 'birthday', 'inactive', 'custom'] as CampaignType[]).map((type) => (
            <div
              key={type}
              onClick={() => handleTypeChange(type)}
              className={`
                relative rounded-lg border p-4 flex flex-col items-center cursor-pointer transition-all
                ${formData.type === type
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 ring-2 ring-blue-300'
                  : 'border-line hover:border-content-3 hover:bg-surface-2'
                }
              `}
            >
              {getCampaignTypeIcon(type)}
              <span className="mt-2 font-medium text-sm text-center" id={`type-label-${type}`}>
                {type === 'all' && 'Todos los Clientes'}
                {type === 'birthday' && 'Cumpleaños'}
                {type === 'inactive' && 'Clientes Inactivos'}
                {type === 'custom' && 'Personalizada'}
              </span>
              <input
                type="radio"
                name="type"
                value={type}
                checked={formData.type === type}
                onChange={() => handleTypeChange(type)}
                className="sr-only"
                aria-labelledby={`type-label-${type}`}
              />
              {formData.type === type && (
                <div className="absolute -top-1 -right-1 h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center text-white shadow">
                  <Check className="h-3 w-3" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          MODO: FIJA vs DINÁMICA — solo cuando la audiencia es una REGLA
          (cumpleaños, inactivos, personalizada por filtros). Con lista manual
          de clientes o "Todos" no aparece: esas son siempre de un disparo.
          ══════════════════════════════════════════════════════════════════════ */}
      {dynamicEligible && (
        <div>
          <label className="block text-sm font-medium text-content-2 mb-2">¿Cómo funciona esta campaña?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              onClick={() => setModeValue('fixed')}
              className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                modeValue === 'fixed'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 ring-2 ring-blue-300'
                  : 'border-line hover:border-content-3 hover:bg-surface-2'
              }`}
            >
              <div className="flex items-center">
                <Pin className="h-4 w-4 text-content-2 flex-shrink-0" />
                <span className="ml-2 font-medium text-sm">📌 Fija (un solo envío)</span>
                {modeValue === 'fixed' && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center text-white shadow"><Check className="h-3 w-3" /></div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-content-3">
                Se toma la foto de hoy: se manda a quienes cumplen la regla ahora y la campaña termina.
              </p>
              <input type="radio" name="campaignMode" value="fixed" checked={modeValue === 'fixed'} onChange={() => setModeValue('fixed')} className="sr-only" />
            </div>
            <div
              onClick={() => setModeValue('dynamic')}
              className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                modeValue === 'dynamic'
                  ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 ring-2 ring-indigo-300'
                  : 'border-line hover:border-content-3 hover:bg-surface-2'
              }`}
            >
              <div className="flex items-center">
                <RefreshCw className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                <span className="ml-2 font-medium text-sm">🔄 Dinámica (siempre activa)</span>
                {modeValue === 'dynamic' && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 bg-indigo-500 rounded-full flex items-center justify-center text-white shadow"><Check className="h-3 w-3" /></div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-content-3">
                Regla viva: cada día entran solos los que empiecen a cumplirla y se omite a quien ya
                no la cumpla. Cada cliente recibe el mensaje una vez por ciclo. No termina hasta que la pauses.
              </p>
              <input type="radio" name="campaignMode" value="dynamic" checked={modeValue === 'dynamic'} onChange={() => setModeValue('dynamic')} className="sr-only" />
            </div>
          </div>
          {modeValue === 'dynamic' && formData.type === 'birthday' && (
            <p className="mt-2 text-xs text-indigo-600 dark:text-indigo-400">
              💡 Con cumpleaños dinámica, cada cliente recibe su aviso automáticamente al entrar al rango
              elegido — todos los meses, sin rehacer la campaña. Ideal para "aparta tu pastel/regalo".
            </p>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          QUIÉN MANDA: el sistema o una persona. Solo aplica a WhatsApp — el
          correo no tiene un número que cuidar y siempre lo manda el sistema.
          ══════════════════════════════════════════════════════════════════════ */}
      {channel !== 'email' && (
        <div>
          <label className="block text-sm font-medium text-content-2 mb-2">¿Quién manda los mensajes?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              onClick={() => setDispatchValue('auto')}
              className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                dispatchValue === 'auto'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 ring-2 ring-blue-300'
                  : 'border-line hover:border-content-3 hover:bg-surface-2'
              }`}
            >
              <div className="flex items-center">
                <Send className="h-4 w-4 text-content-2 flex-shrink-0" />
                <span className="ml-2 font-medium text-sm">🤖 El sistema (automático)</span>
                {dispatchValue === 'auto' && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 bg-blue-500 rounded-full flex items-center justify-center text-white shadow"><Check className="h-3 w-3" /></div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-content-3">
                Salen solos dentro del horario y al ritmo que permite tu número. No tienes que hacer nada.
              </p>
              <input type="radio" name="campaignDispatch" value="auto" checked={dispatchValue === 'auto'} onChange={() => setDispatchValue('auto')} className="sr-only" />
            </div>
            <div
              onClick={() => setDispatchValue('assisted')}
              className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                dispatchValue === 'assisted'
                  ? 'border-amber-500 bg-amber-50 dark:bg-amber-500/10 ring-2 ring-amber-300'
                  : 'border-line hover:border-content-3 hover:bg-surface-2'
              }`}
            >
              <div className="flex items-center">
                <UserCheck className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="ml-2 font-medium text-sm">✋ Tú, uno por uno (manual)</span>
                {dispatchValue === 'assisted' && (
                  <div className="absolute -top-1 -right-1 h-5 w-5 bg-amber-500 rounded-full flex items-center justify-center text-white shadow"><Check className="h-3 w-3" /></div>
                )}
              </div>
              <p className="mt-1.5 text-xs text-content-3">
                Nada sale sin que tú lo pidas: los mandas desde la lista, de a uno. Los manda el
                servidor con los mismos topes, pero tú decides cuándo.
              </p>
              <input type="radio" name="campaignDispatch" value="assisted" checked={dispatchValue === 'assisted'} onChange={() => setDispatchValue('assisted')} className="sr-only" />
            </div>
          </div>
          {dispatchValue === 'assisted' && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Recomendado cuando tu WhatsApp es nuevo o lo acabas de volver a conectar: así ves
              cómo responde antes de soltar el resto.
            </p>
          )}
        </div>
      )}

      {/* ── Config clientes inactivos ── */}
      {formData.type === 'inactive' && (
        <div className="bg-orange-50 dark:bg-orange-500/10 p-4 rounded-lg border border-orange-200 transition-all duration-300 ease-in-out">
          <h3 className="text-sm font-medium text-orange-800 dark:text-orange-300 flex items-center">
            <UserMinus className="h-4 w-4 mr-2" />
            Configuración para Clientes Inactivos
          </h3>
          <div className="mt-3">
            <label htmlFor="inactiveDays" className="block text-sm font-medium text-content-2">
              Días sin visitar
            </label>
            <select
              id="inactiveDays"
              name="inactiveDaysValue"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
              value={formData.filters.find(f => f.type === 'inactiveDays')?.value || 30}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                setFormData(prev => ({
                  ...prev,
                  filters: [{ type: 'inactiveDays', value }]
                }));
              }}
            >
              <option value={15}>15 días</option>
              <option value={30}>30 días</option>
              <option value={45}>45 días</option>
              <option value={60}>2 meses (60 días)</option>
              <option value={90}>3 meses (90 días)</option>
              <option value={120}>4 meses (120 días)</option>
              <option value={180}>6 meses (180 días)</option>
              <option value={270}>9 meses (270 días)</option>
              <option value={365}>1 año (365 días)</option>
            </select>
            <p className="mt-1 text-xs text-orange-600 dark:text-orange-400">
              Se incluirán clientes sin visitas previas y aquellos cuya última visita exceda el plazo seleccionado.
            </p>
          </div>
        </div>
      )}

      {/* ── Config cumpleaños ── */}
      {formData.type === 'birthday' && (
        <div className="bg-pink-50 dark:bg-pink-500/10 p-4 rounded-lg border border-pink-200 transition-all duration-300 ease-in-out">
          <h3 className="text-sm font-medium text-pink-800 dark:text-pink-300 flex items-center">
            <Calendar className="h-4 w-4 mr-2" />
            Configuración para Cumpleaños
          </h3>

          {/* Aviso: solape con el envío automático de cumpleaños */}
          {birthdayAutoEnabled && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-3">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <span className="font-semibold">Ya tienes activo el envío automático de cumpleaños.</span>{' '}
                Si lanzas esta campaña, los clientes que cumplan años estos días podrían recibir el
                mensaje <span className="font-semibold">dos veces</span> (la felicitación automática y esta
                campaña). Considera desactivar el automático en el panel de Cumpleaños o acotar el rango.
              </p>
            </div>
          )}

          <div className="mt-3">
            <label htmlFor="birthdayRangeSelect" className="block text-sm font-medium text-content-2">
              Rango de cumpleaños
            </label>
            <select
              id="birthdayRangeSelect"
              name="birthdayRangeValue"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
              value={birthdayRangeValue}
              onChange={(e) => setBirthdayRangeValue(e.target.value)}
            >
              <option value="0">Solo el día del cumpleaños</option>
              <option value="7">Cumpleaños en los próximos 7 días</option>
              <option value="30">Cumpleaños en los próximos 30 días</option>
            </select>
            {birthdayRangeValue !== '0' && (
              <div className="mt-3">
                <label htmlFor="birthdayMinDaysSelect" className="block text-sm font-medium text-content-2">
                  No avisar si ya faltan menos de…
                </label>
                <select
                  id="birthdayMinDaysSelect"
                  name="birthdayMinDaysValue"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-pink-500 focus:border-pink-500 sm:text-sm rounded-md"
                  value={birthdayMinDaysValue}
                  onChange={(e) => setBirthdayMinDaysValue(e.target.value)}
                >
                  <option value="0">Sin mínimo (incluye a quien cumple hoy)</option>
                  <option value="3">3 días</option>
                  <option value="7">7 días (recomendado para encargos/pre-cumpleaños)</option>
                </select>
                <p className="mt-1 text-xs text-content-3">
                  Evita avisos de último momento: quien ya está a punto de cumplir recibe solo la
                  felicitación automática del día, y este aviso le tocará con tiempo el próximo año.
                </p>
              </div>
            )}
            <p className="mt-1 text-xs text-pink-600 dark:text-pink-400">
              {getBirthdayRangeLabel()}
            </p>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          SECCIÓN DE CAMPAÑA PERSONALIZADA
          ══════════════════════════════════════════════════════════════════════ */}
      {formData.type === 'custom' && (
        <div className="bg-purple-50 dark:bg-purple-500/10 p-4 rounded-lg border border-purple-200 transition-all duration-300 ease-in-out space-y-4">
          <h3 className="text-sm font-medium text-purple-800 dark:text-purple-300 flex items-center">
            <UserPlus className="h-4 w-4 mr-2" />
            Campaña Personalizada
          </h3>

          {/* ── Filtros inteligentes ── */}
          <div className="bg-surface rounded-lg border border-purple-100 p-3 space-y-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-purple-500 flex-shrink-0" />
              <p className="text-sm font-medium text-content-2">Filtros inteligentes</p>
              <span className="text-xs text-content-3">(opcional)</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Filtro: Activos últimos N meses */}
              <div>
                <label htmlFor="activityMonths" className="block text-xs font-medium text-content-2 mb-1">
                  Activos en los últimos
                </label>
                <select
                  id="activityMonths"
                  value={activityMonths}
                  onChange={(e) => setActivityMonths(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-line focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="">Sin filtro de actividad</option>
                  <option value="1">Último mes</option>
                  <option value="3">Últimos 3 meses</option>
                  <option value="6">Últimos 6 meses</option>
                  <option value="12">Último año</option>
                </select>
              </div>

              {/* Filtro: Puntos mínimos */}
              <div>
                <label htmlFor="minPoints" className="block text-xs font-medium text-content-2 mb-1">
                  Puntos / saldo mínimo
                </label>
                <select
                  id="minPoints"
                  value={minPoints}
                  onChange={(e) => setMinPoints(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-sm border-line focus:outline-none focus:ring-purple-500 focus:border-purple-500 rounded-md"
                >
                  <option value="">Sin filtro de puntos</option>
                  {/* Valor fuera de los presets (p.ej. campaña creada por API): mostrarlo,
                      no caer en "Sin filtro" con el filtro realmente activo. */}
                  {minPoints !== '' && !['10', '20', '25', '50', '100', '200', '500'].includes(minPoints) && (
                    <option value={minPoints}>{minPoints}+ puntos (personalizado)</option>
                  )}
                  <option value="10">10+ puntos</option>
                  <option value="20">20+ puntos</option>{/* segmento "frecuentes en riesgo" del dashboard */}
                  <option value="25">25+ puntos</option>
                  <option value="50">50+ puntos</option>
                  <option value="100">100+ puntos</option>
                  <option value="200">200+ puntos</option>
                  <option value="500">500+ puntos</option>
                </select>
              </div>
            </div>

            {/* v9.4: Filtro de recencia por rango (no vienen hace entre X e Y días) */}
            <div>
              <label className="block text-xs font-medium text-content-2 mb-1.5 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Por tiempo sin venir
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-content-3">No vienen hace entre</span>
                <input type="number" min="0" placeholder="—" value={recencyMin}
                  onChange={(e) => setRecencyMin(e.target.value)}
                  className="w-20 px-2 py-1 text-sm border-line rounded-md bg-surface" />
                <span className="text-xs text-content-3">y</span>
                <input type="number" min="0" placeholder="—" value={recencyMax}
                  onChange={(e) => setRecencyMax(e.target.value)}
                  className="w-20 px-2 py-1 text-sm border-line rounded-md bg-surface" />
                <span className="text-xs text-content-3">días</span>
              </div>
              <p className="text-[11px] text-content-3 mt-1">Ej. de 30 a 90 = clientes que llevan entre 1 y 3 meses sin venir. Deja un campo vacío para no acotar ese extremo.</p>
            </div>

            {/* v9.4: Filtro de cumpleañeros por mes calendario */}
            <div>
              <label htmlFor="birthdayMonth" className="block text-xs font-medium text-content-2 mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Cumpleañeros del mes
              </label>
              <select
                id="birthdayMonth"
                value={birthdayMonth}
                onChange={(e) => setBirthdayMonth(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border-line rounded-md bg-surface"
              >
                <option value="">Sin filtro de cumpleaños</option>
                <option value="0">Enero</option>
                <option value="1">Febrero</option>
                <option value="2">Marzo</option>
                <option value="3">Abril</option>
                <option value="4">Mayo</option>
                <option value="5">Junio</option>
                <option value="6">Julio</option>
                <option value="7">Agosto</option>
                <option value="8">Septiembre</option>
                <option value="9">Octubre</option>
                <option value="10">Noviembre</option>
                <option value="11">Diciembre</option>
              </select>
              <p className="text-[11px] text-content-3 mt-1">Todos los que cumplen años en ese mes (mes calendario completo).</p>
            </div>

            {/* v3.2.1: Filtro por etiquetas — SIEMPRE visible */}
            <div>
              <label className="block text-xs font-medium text-content-2 mb-1.5 flex items-center gap-1">
                <Tag className="h-3 w-3" />
                Por etiqueta
              </label>
              {selectedTags.length > 1 && (
                <p className="text-xs text-content-3 mb-1.5">
                  Con varias etiquetas, el cliente debe tener <span className="font-semibold">todas</span> para
                  entrar (intersección — ej. "mundo-silvestre-2026" + "industria-barista" = solo los baristas de esa lista).
                </p>
              )}
              {tagsLoading ? (
                <div className="flex items-center gap-2 text-xs text-content-3">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Cargando etiquetas...
                </div>
              ) : availableTags.length === 0 ? (
                <p className="text-xs text-content-3 italic">
                  Sin etiquetas creadas. Crea etiquetas desde el historial de un cliente o desde Programa → Etiquetas.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {availableTags.map(tag => {
                    const isSelected = selectedTags.includes(tag.name);
                    return (
                      <button
                        key={tag.name}
                        type="button"
                        onClick={() => handleToggleTag(tag.name)}
                        className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
                          isSelected
                            ? 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-300 ring-1 ring-purple-300'
                            : 'bg-surface text-content-2 border-line hover:border-purple-300 hover:bg-purple-50 dark:hover:bg-purple-500/10'
                        }`}
                      >
                        {tag.name}
                        <span className={`ml-1 text-[10px] ${isSelected ? 'text-purple-500' : 'text-content-3'}`}>
                          {tag.count}
                        </span>
                        {isSelected && <X className="h-3 w-3 ml-1" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {hasActiveSmartFilters && (
              <p className="text-xs text-purple-600 dark:text-purple-400">
                {(() => {
                  const parts: string[] = [];
                  if (activityMonths) parts.push(`activos en los últimos ${activityMonths} mes${activityMonths !== '1' ? 'es' : ''}`);
                  if (minPoints) parts.push(`con ${minPoints}+ puntos`);
                  if (selectedTags.length > 0) parts.push(`etiquetados como "${selectedTags.join('", "')}"`);
                  return `Se incluirán clientes ${parts.join(' y ')}.`;
                })()}
                {selectedClients.length > 0 && ' Además de los clientes seleccionados manualmente.'}
              </p>
            )}
          </div>

          {/* ── Buscar cliente individual ── */}
          <div className="border-t border-purple-200 pt-4">
            <label className="block text-sm font-medium text-content-2 mb-1">
              Buscar y agregar clientes
            </label>
            <SearchInput
              onSearch={() => {}}
              onClientSelect={handleClientSelect}
              placeholder="Buscar por nombre o teléfono..."
              showResults={true}
              preventClientNavigation={true}
              debounceTime={400}
              minSearchLength={2}
              className="w-full"
            />
          </div>

          {/* ── Importar de campaña anterior ── */}
          <div className="border-t border-purple-200 pt-4">
            <label className="block text-sm font-medium text-content-2 mb-1">
              Importar de una campaña anterior
            </label>
            <div className="flex gap-2">
              <select
                value={selectedImportCampaignId}
                onChange={(e) => setSelectedImportCampaignId(e.target.value)}
                disabled={importLoading || campaignsLoading}
                className="flex-1 block w-full pl-3 pr-10 py-2 text-base border-line focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md disabled:opacity-50"
              >
                <option value="">
                  {campaignsLoading ? 'Cargando campañas...' : 'Seleccionar campaña...'}
                </option>
                {importableCampaigns.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.totalMessagesSent} enviados)
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleImportFromCampaign}
                disabled={!selectedImportCampaignId || importLoading}
                className="inline-flex items-center px-3 py-2 border border-purple-300 rounded-md text-sm font-medium text-purple-700 dark:text-purple-300 bg-surface hover:bg-purple-50 dark:hover:bg-purple-500/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span className="ml-1.5 hidden sm:inline">Importar</span>
              </button>
            </div>
            <p className="mt-1 text-xs text-purple-600 dark:text-purple-400">
              Se importarán los clientes a quienes se les envió mensaje en esa campaña.
            </p>
          </div>

          {/* ── Lista de clientes seleccionados ── */}
          {selectedClients.length > 0 && (
            <div className="border-t border-purple-200 pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-content-2">
                  {selectedClients.length} cliente{selectedClients.length !== 1 ? 's' : ''} seleccionado{selectedClients.length !== 1 ? 's' : ''}
                  {importedCount > 0 && namedCount > 0 && (
                    <span className="text-xs text-content-3 ml-1">
                      ({namedCount} buscado{namedCount !== 1 ? 's' : ''}, {importedCount} importado{importedCount !== 1 ? 's' : ''})
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={handleClearAllClients}
                  className="text-xs text-red-500 hover:text-red-700 dark:text-red-300 hover:underline"
                >
                  Limpiar todo
                </button>
              </div>

              <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                {selectedClients.map(client => (
                  <div
                    key={client.id}
                    className="flex items-center justify-between bg-surface rounded-md border border-line px-3 py-2 text-sm"
                  >
                    <div className="flex items-center min-w-0">
                      <div className="h-6 w-6 rounded-full bg-purple-100 dark:bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        <Users className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="ml-2 truncate">
                        {client.name ? (
                          <span className="font-medium text-content">{client.name}</span>
                        ) : (
                          <span className="text-content-3 italic">Cliente importado</span>
                        )}
                        {client.phone && (
                          <span className="text-content-3 ml-2 text-xs">{client.phone}</span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveClient(client.id)}
                      className="ml-2 p-1 text-content-3 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-full flex-shrink-0 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Estado vacío ── */}
          {selectedClients.length === 0 && !hasActiveSmartFilters && (
            <div className="text-center py-4 text-sm text-purple-400">
              <Search className="h-6 w-6 mx-auto mb-1 opacity-50" />
              Configura filtros, busca clientes o importa de una campaña anterior
            </div>
          )}
        </div>
      )}

      {/* ── Mensaje ── */}
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-content-2">
          Mensaje <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <textarea
            id="message"
            name="message"
            rows={4}
            value={formData.message}
            onChange={handleInputChange}
            className="block w-full rounded-md border-line shadow-sm focus:border-blue-500 focus:ring-accent sm:text-sm"
            placeholder="Escribe el mensaje que se enviará a los clientes..."
          />
        </div>
        <p className="mt-2 text-sm text-content-3">
          Variables disponibles:
          <span className="font-mono bg-canvas px-1.5 py-0.5 rounded mx-1 text-xs">@nombre</span>
          <span className="font-mono bg-canvas px-1.5 py-0.5 rounded mx-1 text-xs">@marca</span>
          <span className="font-mono bg-canvas px-1.5 py-0.5 rounded mx-1 text-xs">@puntos</span>
        </p>
        {formData.message.trim() !== '' && !/@(nombre|marca|puntos)/.test(formData.message) && (
          <div className="mt-2 bg-amber-50 p-3 rounded-md border border-amber-200">
            <p className="text-sm text-amber-700">
              ⚠️ Tu mensaje no usa ninguna variable. Los mensajes idénticos enviados a muchos
              clientes tienen más riesgo de ser marcados como spam por WhatsApp. Te recomendamos
              personalizarlo, por ejemplo empezando con <span className="font-mono">Hola @nombre</span>.
            </p>
          </div>
        )}
      </div>

      {/* ── Imagen adjunta ── */}
      {isPremiumWaha && (
        <div className="rounded-lg border border-line bg-surface-2 p-4 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-4 w-4 text-purple-500 flex-shrink-0" />
            <p className="text-sm font-medium text-content-2">Imagen adjunta</p>
            <span className="text-xs text-content-3">(opcional)</span>
          </div>
          <ImageAttachment
            imageBase64={formData.image ?? null}
            onImageChange={handleImageChange}
            label=""
            helpText="Se enviará junto al mensaje en el envío directo por WhatsApp"
            disabled={loading}
          />
        </div>
      )}

      {/* Aquí vivía un bloque «Por favor, corrige los siguientes errores» alimentado por
          `validationErrors`, que el ErrorContext nunca ha expuesto: con el `|| []` de guarda
          quedaba siempre vacío y jamás se renderizó. Los errores de validación de este
          formulario salen por addValidationError → ErrorDisplay (aviso flotante). */}

      {/* ── Botones ── */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-line">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || !formData.name.trim() || !formData.message.trim()}
          className="inline-flex justify-center items-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Guardando...' : (initialData ? 'Actualizar Campaña' : 'Crear Campaña')}
        </button>
      </div>
    </form>
  );
};

export default CampaignForm;